import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Wallet, TrendingUp, TrendingDown, Users, CheckCircle, XCircle, Clock, Plus, X, Trash2 } from "lucide-react";
import { confirmAlert } from "../../../components/ui/confirmAlert";
import { handleGet } from "../../../controllers/controller.all";
import { Get, Send, Remove } from "../../../services/service.api";
import { toast } from "react-toastify";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

const STATUS_COLORS = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
    successful: "bg-emerald-50 text-emerald-700 border-emerald-100",
    pending_validation: "bg-violet-50 text-violet-700 border-violet-100",
    pending: "bg-amber-50 text-amber-700 border-amber-100",
    rejected: "bg-red-50 text-red-700 border-red-100"
};

const STATUS_LABELS = {
    approved: "Approuvé",
    successful: "Approuvé",
    pending_validation: "En vérification",
    pending: "En attente",
    rejected: "Rejeté"
};

export default function FinanceList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;

    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [annees, setAnnees] = useState([]);
    const [selectedAnnee, setSelectedAnnee] = useState("");

    // Manual payment modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [manualAmount, setManualAmount] = useState("");
    const [manualDescription, setManualDescription] = useState("Paiement de scolarité");
    const [manualTag, setManualTag] = useState("scolarité");
    const [savingManual, setSavingManual] = useState(false);

    // Expand class section
    const [expandedClass, setExpandedClass] = useState(null);

    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("prenom");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 5;

    const fetchPayments = async () => {
        try {
            const query = selectedAnnee ? `?annee=${selectedAnnee}` : "";
            const res = await Get(`/payment/ecole/${ecoleId}${query}`, token);
            if (res && res.data) setPayments(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        if (ecoleId) {
            await handleGet(`/classe/ecole/${ecoleId}`, (res) => {
                if (res?.classes) setClasses(res.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
            }, token);
            await handleGet(`/user/s/all/eleves/${ecoleId}`, (res) => {
                if (res?.users) setStudents(res.users);
            }, token);
            await handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
                if (res?.annees) setAnnees(res.annees);
            }, token);
            await fetchPayments();
        }
        setLoading(false);
    };

    useEffect(() => {
        if (token) fetchData();
        // eslint-disable-next-line
    }, [ecoleId, token]);

    useEffect(() => {
        if (token && !loading) {
            fetchPayments();
        }
        // eslint-disable-next-line
    }, [selectedAnnee]);

    // Aggregate payments per student
    const getStudentPayments = (studentId) =>
        payments.filter(p => p.user_id === studentId);

    const getStudentTotal = (studentId) =>
        payments
            .filter(p => p.user_id === studentId && (p.status === "approved" || p.status === "successful") && (p.tag === "scolarité" || !p.tag))
            .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

    // Group students by class
    const dataByClass = classes.map(cls => {
        const clsStudents = students.filter(s => (s.classe?._id || s.classe) === cls._id);
        const totalExpected = cls.montant ? (cls.montant * clsStudents.length) : 0;
        const totalCollected = clsStudents.reduce((acc, s) => acc + getStudentTotal(s._id), 0);
        const totalRemaining = totalExpected - totalCollected;

        return {
            ...cls,
            students: clsStudents,
            stats: { count: clsStudents.length, totalExpected, totalCollected, totalRemaining }
        };
    }).filter(c => c.students.length > 0);

    const globalStats = {
        totalCollected: dataByClass.reduce((acc, c) => acc + c.stats.totalCollected, 0),
        totalRemaining: dataByClass.reduce((acc, c) => acc + c.stats.totalRemaining, 0),
        totalStudents: students.length,
        pendingValidation: payments.filter(p => p.status === "pending_validation").length
    };

    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    // Apply sorting to students inside classes
    const sortedDataByClass = dataByClass.map(cls => {
        const sortedStudents = [...cls.students].sort((a, b) => {
            let valA = a[sortKey] || "";
            let valB = b[sortKey] || "";
            if (sortKey === "paid") {
                valA = getStudentTotal(a._id);
                valB = getStudentTotal(b._id);
            }
            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return { ...cls, students: sortedStudents };
    });

    const paginatedClasses = sortedDataByClass.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleStatusUpdate = async (paymentId, newStatus) => {
        setUpdatingId(paymentId);
        try {
            const res = await Send(`/payment/${paymentId}/status`, { status: newStatus }, true, token);
            if (res && res.status === "success") {
                toast.success(`Paiement ${newStatus === "approved" ? "approuvé" : "rejeté"}`);
                await fetchPayments();
            } else {
                toast.error("Erreur lors de la mise à jour");
            }
        } catch (e) {
            toast.error("Erreur réseau");
        }
        setUpdatingId(null);
    };

    const handleDeletePayment = async (paymentId) => {
        const isConfirmed = await confirmAlert({
            title: "Suppression",
            message: "Voulez-vous vraiment supprimer ce paiement ?",
            confirmText: "Supprimer",
            type: "danger"
        });
        if (!isConfirmed) return;
        setDeletingId(paymentId);
        try {
            const res = await Remove(`/payment/${paymentId}`, token);
            if (res && (res.status === "success" || res.state === "success")) {
                toast.success(res.message || "Paiement supprimé");
                await fetchPayments();
            } else {
                toast.error(res?.message || res?.detail?.error || "Erreur lors de la suppression du paiement");
            }
        } catch (e) {
            toast.error("Erreur réseau");
        }
        setDeletingId(null);
    };

    const handleAddManualPayment = async (e) => {
        e.preventDefault();
        if (!selectedStudent || !manualAmount) return;
        setSavingManual(true);
        try {
            const payload = {
                amount: Number(manualAmount),
                description: manualDescription,
                customer_email: selectedStudent.email || "",
                customer_name: selectedStudent.prenom || "",
                customer_surname: selectedStudent.nom || "",
                user_id: selectedStudent._id,
                ecole: ecoleId,
                classe: selectedStudent.classe?._id || selectedStudent.classe,
                tag: manualTag,
                method: "admin",
                reference: `ADMIN-${Date.now()}`
            };
            const res = await Send("/payment/manual", payload, true, token);
            if (res && res.status === "success") {
                // Immediately approve it since admin is adding it manually
                if (res.data?.transaction_id) {
                    // Find the newly created payment and approve it
                    await new Promise(r => setTimeout(r, 500));
                    await fetchPayments();
                    const newPayment = payments.find(p => p.transaction_id === res.data.transaction_id);
                    if (newPayment?._id) {
                        await Send(`/payment/${newPayment._id}/status`, { status: "approved" }, true, token);
                    }
                }
                toast.success("Paiement enregistré et approuvé");
                setShowAddModal(false);
                setManualAmount("");
                setSelectedStudent(null);
                await fetchPayments();
            } else {
                toast.error("Erreur lors de l'enregistrement");
            }
        } catch (e) {
            toast.error("Erreur réseau");
        }
        setSavingManual(false);
    };

    return (
        <div className="flex flex-col gap-8 pb-12">
            <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900">Suivi des Scolarités</h1>
                    <p className="text-gray-500 mt-1">Supervisez les paiements, validations et restes à payer par classe.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex flex-col gap-1.5 min-w-[150px]">
                        <select
                            value={selectedAnnee}
                            onChange={(e) => setSelectedAnnee(e.target.value)}
                            className="appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Année Active</option>
                            {annees.map((a) => (
                                <option key={a._id} value={a.annee}>{a.annee}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center justify-center gap-2 bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                    >
                        <Plus className="h-4 w-4" /> Saisir un paiement
                    </button>
                </div>
            </header>

            {/* Global Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
                <div className="bg-white p-4 sm:p-5 lg:p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3 lg:gap-4 min-w-0">
                    <div className="p-2.5 lg:p-3 bg-indigo-50 text-indigo-600 rounded-xl flex-shrink-0"><Users className="h-5 w-5 lg:h-6 lg:w-6" /></div>
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">Total Étudiants</p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{globalStats.totalStudents}</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-5 lg:p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3 lg:gap-4 min-w-0">
                    <div className="p-2.5 lg:p-3 bg-emerald-50 text-emerald-600 rounded-xl flex-shrink-0"><TrendingUp className="h-5 w-5 lg:h-6 lg:w-6" /></div>
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">Total Collecté</p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-600">{globalStats.totalCollected.toLocaleString()} FCFA</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-5 lg:p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3 lg:gap-4 min-w-0">
                    <div className="p-2.5 lg:p-3 bg-red-50 text-red-600 rounded-xl flex-shrink-0"><TrendingDown className="h-5 w-5 lg:h-6 lg:w-6" /></div>
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">Restant à Percevoir</p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">{globalStats.totalRemaining.toLocaleString()} FCFA</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-5 lg:p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3 lg:gap-4 min-w-0">
                    <div className="p-2.5 lg:p-3 bg-violet-50 text-violet-600 rounded-xl flex-shrink-0"><Clock className="h-5 w-5 lg:h-6 lg:w-6" /></div>
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">En vérification</p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-violet-600">{globalStats.pendingValidation}</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Calcul des finances en cours...</p>
                </div>
            ) : dataByClass.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-200 text-center">
                    <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">Aucune donnée financière</h3>
                    <p className="text-gray-500">Assurez-vous d'avoir des classes avec des montants définis et des étudiants assignés.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {paginatedClasses.map((cls) => (
                        <div key={cls._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            {/* Class Header */}
                            <div
                                className="bg-gray-50/50 px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                                onClick={() => setExpandedClass(expandedClass === cls._id ? null : cls._id)}
                            >
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <div className="w-2 h-6 bg-indigo-500 rounded-full"></div>
                                        Classe : {cls.libelle}
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-0.5">{cls.stats.count} étudiants • Tarif : {Number(cls.montant || 0).toLocaleString()} FCFA</p>
                                </div>
                                <div className="flex items-center gap-4 divide-x divide-gray-200">
                                    <div className="px-4">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Perçu</p>
                                        <p className="text-sm font-bold text-emerald-600">{cls.stats.totalCollected.toLocaleString()} FCFA</p>
                                    </div>
                                    <div className="px-4 text-right">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Dette</p>
                                        <p className="text-sm font-bold text-red-600">{cls.stats.totalRemaining.toLocaleString()} FCFA</p>
                                    </div>
                                </div>
                            </div>

                            {/* Student Table */}
                            {(expandedClass === cls._id || expandedClass === null) && (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50/30">
                                            <tr>
                                                <SortableHeader label="Étudiant" sortKey="prenom" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                                <SortableHeader label="Montant Payé" sortKey="paid" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reliquat</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Paiements</th>
                                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {cls.students.map((student) => {
                                                const paid = getStudentTotal(student._id);
                                                const studentPayments = getStudentPayments(student._id);
                                                const total = Number(cls.montant) || 0;
                                                const balance = total - paid;
                                                const isFullyPaid = balance <= 0;

                                                return (
                                                    <tr key={student._id} className="hover:bg-gray-50/50 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{student.prenom} {student.nom}</div>
                                                            <div className="text-xs text-gray-400">{student.numero_etudiant || "Pas de matricule"}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-semibold text-gray-900">{paid.toLocaleString()} FCFA</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className={`text-sm font-semibold ${isFullyPaid ? "text-gray-400" : "text-red-500"}`}>
                                                                {isFullyPaid ? "Réglé" : `${balance.toLocaleString()} FCFA`}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-col gap-1 max-w-xs">
                                                                {studentPayments.length === 0 ? (
                                                                    <span className="text-xs text-gray-400 italic">Aucun paiement</span>
                                                                ) : studentPayments.map(p => (
                                                                    <div key={p._id} className="flex items-center gap-1.5 p-1.5 rounded-lg bg-gray-50/80 border border-gray-100 hover:border-gray-200 transition-colors">
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[p.status] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                                                                            {STATUS_LABELS[p.status] || p.status}
                                                                        </span>
                                                                        <span className="text-xs text-gray-800 font-semibold">{Number(p.amount || 0).toLocaleString()} XOF</span>
                                                                        <span className="text-xs text-gray-400 capitalize">{p.tag || "scolarité"}</span>
                                                                        <div className="flex items-center gap-1 ml-auto">
                                                                            {p.status === "pending_validation" && (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => handleStatusUpdate(p._id, "approved")}
                                                                                        disabled={updatingId === p._id || deletingId === p._id}
                                                                                        className="p-1 rounded-md text-emerald-600 hover:bg-emerald-100 transition-colors"
                                                                                        title="Approuver"
                                                                                    >
                                                                                        <CheckCircle className="h-3.5 w-3.5" />
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleStatusUpdate(p._id, "rejected")}
                                                                                        disabled={updatingId === p._id || deletingId === p._id}
                                                                                        className="p-1 rounded-md text-red-500 hover:bg-red-100 transition-colors"
                                                                                        title="Rejeter"
                                                                                    >
                                                                                        <XCircle className="h-3.5 w-3.5" />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                            <button
                                                                                onClick={() => handleDeletePayment(p._id)}
                                                                                disabled={deletingId === p._id || updatingId === p._id}
                                                                                className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                                                                title="Supprimer ce paiement"
                                                                            >
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                                            <button
                                                                onClick={() => { setSelectedStudent(student); setShowAddModal(true); }}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors"
                                                            >
                                                                <Plus className="h-3.5 w-3.5" />
                                                                Ajouter
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                    {dataByClass.length > itemsPerPage && (
                        <Pagination 
                            currentPage={currentPage} 
                            totalItems={dataByClass.length} 
                            itemsPerPage={itemsPerPage} 
                            onPageChange={setCurrentPage} 
                        />
                    )}
                </div>
            )}

            {/* Manual Payment Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold text-gray-900">
                                Ajouter un paiement
                                {selectedStudent && <span className="block text-sm font-normal text-gray-500 mt-0.5">{selectedStudent.prenom} {selectedStudent.nom}</span>}
                            </h3>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleAddManualPayment} className="flex flex-col gap-4">
                            {/* Student Selector */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Élève *</label>
                                <select
                                    required
                                    value={selectedStudent?._id || ""}
                                    onChange={e => {
                                        const st = students.find(s => s._id === e.target.value);
                                        setSelectedStudent(st || null);
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                >
                                    <option value="">-- Choisir un élève --</option>
                                    {students.map(s => (
                                        <option key={s._id} value={s._id}>
                                            {s.prenom} {s.nom}
                                            {s.classe?.libelle ? ` — ${s.classe.libelle}` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type de paiement</label>
                                <div className="flex gap-2">
                                    {["scolarité", "cotisation", "don"].map(t => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setManualTag(t)}
                                            className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize border transition-colors ${manualTag === t ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50"}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Montant (FCFA) *</label>
                                <input
                                    type="number"
                                    required
                                    value={manualAmount}
                                    onChange={e => setManualAmount(e.target.value)}
                                    placeholder="Ex: 50000"
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Motif</label>
                                <input
                                    type="text"
                                    value={manualDescription}
                                    onChange={e => setManualDescription(e.target.value)}
                                    placeholder="Ex: Scolarité Trimestre 1"
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                                    Annuler
                                </button>
                                <button type="submit" disabled={savingManual} className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60">
                                    {savingManual ? "Enregistrement..." : "Enregistrer & Approuver"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
